import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { isDeadlinePassed, parisToday } from '../utils/interventionDate';
import {
  acceptMyInterventionRequest,
  declineMyInterventionRequest,
  fetchMyInterventionRequests,
  markMyInterventionRequestSeen,
  type AcceptSlot,
  type DeclineRequestInput,
  type MyInterventionRequest,
} from '../api/myInterventionRequestsApi';

const KEY = 'my-intervention-requests';

/**
 * The technician's own requests. `refetchOnMount` is on: the WebView keeps the page alive across
 * tab switches, and a stale list is exactly how he ends up answering a mission somebody else was
 * already given — the wrong this screen exists to prevent.
 *
 * `enabled` because the `/issues` segment reads the same key for its badge, and the four other
 * roles that share that screen have nothing here to ask for.
 */
export const useMyInterventionRequests = (enabled = true) =>
  useQuery<MyInterventionRequest[]>({
    queryKey: [KEY],
    queryFn: fetchMyInterventionRequests,
    refetchOnMount: true,
    enabled,
  });

/**
 * Written out twice rather than behind a generic wrapper, as `useUnavailabilities` already is: the
 * two differ by their payload, and a wrapper would be one more abstraction instead of one less
 * copy.
 */
export const useAcceptMyInterventionRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...slot }: { id: number } & AcceptSlot) => acceptMyInterventionRequest(id, slot),
    // The app default retries a mutation once. Nothing here is idempotent: a replayed POST whose
    // response was lost but whose write landed comes back 409 `ALREADY_ANSWERED`, and the screen
    // would report a failure for an answer that is recorded.
    retry: false,
    // `onSettled` and not `onSuccess`: a 409 is precisely when the list is stale — re-reading it
    // is what makes the action footer disappear instead of offering an answer twice.
    onSettled: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useDeclineMyInterventionRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: DeclineRequestInput & { id: number }) =>
      declineMyInterventionRequest(id, input),
    retry: false,
    onSettled: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
};

/**
 * He opened the detail (E15): clears the amber badge. Fire and forget — a 404 (foreign id) or a
 * network blip must never surface on a screen he opened to read.
 */
export const useMarkSeen = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => markMyInterventionRequestSeen(id),
    retry: false,
    // No `onError`: `mutate` never throws, and no screen reads this mutation's error.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
};

/** Reminded and not opened since the last reminder — a later reminder re-arms it (E15). */
export const hasUnseenReminder = ({ reminderCount, seenAt, lastReminderAt }: MyInterventionRequest): boolean =>
  reminderCount > 0 && (!seenAt || (!!lastReminderAt && seenAt < lastReminderAt));

/** A stored status, or a derived one (E18): reminded and unseen, or deadline passed. */
export type RequestVariant = MyInterventionRequest['status'] | 'reminded' | 'overdue';

/** Derived, never stored — « Échéance dépassée » wins, as on the ops side (vocabulary.ts). */
export const requestVariant = (request: MyInterventionRequest, today: string = parisToday()): RequestVariant => {
  if (request.status !== 'pending') return request.status;
  if (isDeadlinePassed(request.deadlineDate, today)) return 'overdue';
  return hasUnseenReminder(request) ? 'reminded' : 'pending';
};

/**
 * The badge (E15): requests still waiting on him, and among them the reminders he has not seen —
 * the amber cards, so an amber badge always points at one. One definition for Header and IssuesList.
 */
export const pendingBadge = (
  requests: MyInterventionRequest[] | null | undefined
): { pending: number; unseenReminders: number } => {
  const today = parisToday();
  const pending = (requests ?? []).filter((request) => request.status === 'pending');
  return {
    pending: pending.length,
    unseenReminders: pending.filter((request) => requestVariant(request, today) === 'reminded').length,
  };
};

/** « 3 demandes en attente…, dont 1 relancée » — two keys, each pluralized on its own count. */
export const pendingBadgeLabel = (t: TFunction, { pending, unseenReminders }: ReturnType<typeof pendingBadge>): string =>
  t('{{count}} request awaiting your answer', { count: pending }) +
  (unseenReminders > 0 ? t(', {{count}} reminded', { count: unseenReminders }) : '');
